'use client';

import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import IconAdaptive from '@/components/IconAdaptive';

export interface MapPinButtonUnitProps {
    label: string;
    icon: LucideIcon;
    active: boolean;
    colorClass: string;
    hovered: boolean;
    setHovered: (v: boolean) => void;
    onClick: () => void;
    onResetView?: () => void;
    isMobile: boolean;
    isTablet: boolean;
    mobileWidth: number;
    positionBottom: number;
    positionBottomMd: number;
    hoveredWidth?: number;
}

export default function HubMapPinButtonUnit({
    label,
    icon: Icon,
    active,
    colorClass,
    hovered,
    setHovered,
    onClick,
    onResetView,
    isMobile,
    isTablet,
    mobileWidth,
    positionBottom,
    positionBottomMd,
    hoveredWidth = 100,
}: MapPinButtonUnitProps) {
    const activeBg = `bg-color-${colorClass}`;
    const activeText = 'text-color-white-base';
    const inactiveBg = 'bg-color-white-base';
    const inactiveText = `text-color-${colorClass}`;

    const isMobileView = isMobile || isTablet;

    if (isMobileView) {
        return (
            <div
                className={`keen-slider__slide shrink-0 flex flex-row items-center backdrop-blur-md p-1 rounded
                    ${active ? `${activeBg} ${activeText}` : `${inactiveBg} ${inactiveText}`}
                `}
                style={{ width: `${mobileWidth}px` }}
                onClick={onClick}
            >
                <IconAdaptive Icon={Icon} baseSize={18} className={active ? 'text-color-white-base' : inactiveText} />
                <span className="font-montserrat font-bold text-xs ml-2 whitespace-nowrap">{label}</span>
            </div>
        );
    }

    const bottomClass = `bottom-[${positionBottom}px] md:bottom-[${positionBottomMd}px]`;
    return (
        <motion.div
            style={{
                bottom: positionBottomMd,
            }}
            className={`
                absolute right-2 z-10 flex flex-row-reverse items-center backdrop-blur-md p-2
                rounded shadow-md overflow-hidden cursor-pointer
                ${bottomClass}
                ${active ? `${activeBg} ${activeText}` : `${inactiveBg} ${inactiveText}`}
            `}
            initial={{ width: 37 }}
            animate={{ width: hovered ? hoveredWidth : 37 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
        >
            <IconAdaptive
                Icon={Icon}
                baseSize={20}
                className="cursor-pointer"
                onClick={() => {
                    if (onResetView) {
                        onResetView();
                    }
                }}
            />

            <motion.span
                className="font-montserrat font-bold text-sm mr-2 whitespace-nowrap text-left w-full"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : 10 }}
                transition={{ duration: 0.2 }}
            >
                {label}
            </motion.span>
        </motion.div>
    );
}
